import CloseIcon from "@mui/icons-material/Close";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import GridOnIcon from "@mui/icons-material/GridOn";
import * as MUI from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useState } from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { spreadsheetTypes } from "../../../../enums/Consts";

const ACCEPT = {
  [spreadsheetTypes[0]]: [".xls"],
  [spreadsheetTypes[1]]: [".xlsx"],
};
const EXPECTED_COLUMNS = [
  "ImportColumnTitle",
  "ImportColumnAddress",
  "ImportColumnPhoneNumber",
  "ImportColumnEmail",
];

function formatSize(bytes) {
  return bytes < 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024))} KB`
    : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function SelectedFile({ file, onRemove }) {
  const { t } = useTranslation();
  return (
    <MUI.Stack
      direction="row"
      alignItems="center"
      spacing={2}
      sx={{
        p: 2,
        borderRadius: 3,
        border: "1px solid",
        borderColor: "success.main",
        bgcolor: (theme) => alpha(theme.palette.success.main, 0.08),
      }}
    >
      <MUI.Avatar
        variant="rounded"
        sx={{ bgcolor: "success.main", color: "common.white" }}
      >
        <GridOnIcon />
      </MUI.Avatar>
      <MUI.Box sx={{ minWidth: 0, flex: 1 }}>
        <MUI.Typography fontWeight={600} noWrap title={file.name}>
          {file.name}
        </MUI.Typography>
        <MUI.Typography variant="body2" color="text.secondary">
          {formatSize(file.size)}
        </MUI.Typography>
      </MUI.Box>
      <MUI.Tooltip title={t("Remove")}>
        <MUI.IconButton onClick={onRemove} size="small">
          <CloseIcon fontSize="small" />
        </MUI.IconButton>
      </MUI.Tooltip>
    </MUI.Stack>
  );
}

function DropArea({ onFileChange }) {
  const { t } = useTranslation();
  const [isRejected, setIsRejected] = useState(false);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: ACCEPT,
    maxFiles: 1,
    multiple: false,
    onDropAccepted: ([file]) => {
      setIsRejected(false);
      onFileChange(file);
    },
    onDropRejected: () => setIsRejected(true),
  });
  const accent = isRejected ? "error" : "primary";

  return (
    <MUI.Stack
      {...getRootProps()}
      alignItems="center"
      spacing={1.5}
      sx={{
        py: 5,
        px: 2,
        textAlign: "center",
        cursor: "pointer",
        borderRadius: 3,
        border: "2px dashed",
        borderColor: isDragActive || isRejected ? `${accent}.main` : "divider",
        bgcolor: (theme) =>
          isDragActive || isRejected
            ? alpha(theme.palette[accent].main, 0.08)
            : "transparent",
        transition: "border-color 150ms, background-color 150ms",
        "&:hover": {
          borderColor: `${accent}.main`,
          bgcolor: (theme) => alpha(theme.palette[accent].main, 0.04),
        },
      }}
    >
      <input {...getInputProps()} />
      <MUI.Avatar
        sx={{
          width: 56,
          height: 56,
          bgcolor: (theme) => alpha(theme.palette[accent].main, 0.15),
          color: `${accent}.main`,
        }}
      >
        <CloudUploadOutlinedIcon fontSize="large" />
      </MUI.Avatar>
      <MUI.Typography variant="subtitle1" fontWeight={600}>
        {t(isDragActive ? "DropSpreadsheetActive" : "DropSpreadsheetHere")}
      </MUI.Typography>
      <MUI.Typography variant="body2" color="text.secondary">
        {t("OrClickToBrowse")}
      </MUI.Typography>
      <MUI.Typography
        variant="caption"
        color={isRejected ? "error.main" : "text.secondary"}
      >
        {t(isRejected ? "UnsupportedSpreadsheet" : "SpreadsheetFileTypes")}
      </MUI.Typography>
    </MUI.Stack>
  );
}

export default function SpreadsheetDropzone({ file, onFileChange }) {
  const { t } = useTranslation();
  return (
    <MUI.Stack spacing={2}>
      {file ? (
        <SelectedFile file={file} onRemove={() => onFileChange(null)} />
      ) : (
        <DropArea onFileChange={onFileChange} />
      )}
      <MUI.Stack spacing={1}>
        <MUI.Typography variant="body2" fontWeight={600}>
          {t("ExpectedColumns")}
        </MUI.Typography>
        <MUI.Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {EXPECTED_COLUMNS.map((column) => (
            <MUI.Chip key={column} size="small" label={t(column)} />
          ))}
        </MUI.Stack>
        <MUI.Typography variant="caption" color="text.secondary">
          {t("ImportClinicsAddressFormat")}
        </MUI.Typography>
      </MUI.Stack>
    </MUI.Stack>
  );
}
